import React, { useState, useEffect, useCallback } from 'react';
import { Card, Steps, Button, message, Space, Spin, Alert } from 'antd';
import { DealsApi } from 'api/api';
import {
  UpdateDealCommand,
  CreateUpdateDealUnitCommand,
  DealStage,
  DealBasicDetailsDto,
  DealUnitDto,
  DealPaymentsDto
} from 'api/models';
import { useTranslation } from 'react-i18next';
import { PageHeader } from 'components/page-headers/page-headers';
import { Main } from 'container/styled';
import ProjectAndUnitSelection from './components/DealSelectionSteps/ProjectAndUnitSelection';
import ClientSelection from './components/DealSelectionSteps/ClientSelection';
import styled from 'styled-components';
import FeatherIcon from 'feather-icons-react';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';

const StyledForm = styled.div`
  .ant-card {
    margin-bottom: 24px;
  }
`;

const StepsContent = styled.div`
  min-height: 400px;
  margin-top: 24px;
  padding: 24px;
  background-color: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 2px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  min-height: 200px;
`;

const dealsApi = new DealsApi();

interface LoadingStates {
  basic: boolean;
  units: boolean;
  payments: boolean;
}

const DealUpdatePage = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Separate loading states for different data sections
  const [loadingStates, setLoadingStates] = useState<LoadingStates>({
    basic: true,
    units: false,
    payments: false,
  });

  const [submitting, setSubmitting] = useState(false);
  const [dealBasic, setDealBasic] = useState<DealBasicDetailsDto>();
  const [dealUnits, setDealUnits] = useState<DealUnitDto[]>([]);
  const [dealPayments, setDealPayments] = useState<DealPaymentsDto>();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<Partial<UpdateDealCommand>>({});
  const [canEdit, setCanEdit] = useState<boolean | null>(null);

  // Helper function to update loading state
  const updateLoadingState = useCallback((section: keyof LoadingStates, loading: boolean) => {
    setLoadingStates(prev => ({ ...prev, [section]: loading }));
  }, []);

  // Fetch basic deal details first
  const fetchBasicDetails = useCallback(async () => {
    try {
      updateLoadingState('basic', true);
      const response = await dealsApi.apiDealsIdGet({ id: Number(id) });
      setDealBasic(response.data);

      // Set initial form data with basic info
      setFormData(prev => ({
        ...prev,
        id: response.data.id,
        projectId: response.data.projectId,
        clientId: response.data.clientId,
      }));
    } catch (error) {
      message.error(t('deals:fetch-error', 'Failed to fetch deal details'));
      navigate('/deals');
    } finally {
      updateLoadingState('basic', false);
    }
  }, [id, t, navigate, updateLoadingState]);

  // Fetch deal units
  const fetchUnits = useCallback(async () => {
    if (!dealBasic) return;

    try {
      updateLoadingState('units', true);
      const response = await dealsApi.apiDealsIdUnitsGet({ id: Number(id) });
      setDealUnits(response.data);

      // Update form data with units
      setFormData(prev => ({
        ...prev,
        units: response.data.map(unit => ({
          unitId: unit.unitId,
          name: unit.unitName,
          quantity: unit.quantity,
          negotiatedPricePerSqm: unit.negotiatedPricePerSqm,
          totalPrice: unit.totalPrice,
          notes: unit.notes,
          squareMeters: unit.squareMeters,
          floorAllocations: unit.floors?.map(floor => ({
            unitFloorId: floor.unitFloorId,
            stock: floor.quantity,
          })),
        })) as CreateUpdateDealUnitCommand[],
      }));
    } catch (error) {
      message.error(t('deals:fetch-units-error', 'Failed to fetch deal units'));
    } finally {
      updateLoadingState('units', false);
    }
  }, [id, dealBasic, t, updateLoadingState]);

  // Fetch deal payments (to check if deal can be edited)
  const fetchPayments = useCallback(async () => {
    if (!dealBasic) return;

    try {
      updateLoadingState('payments', true);
      const response = await dealsApi.apiDealsIdPaymentsGet({ id: Number(id) });
      setDealPayments(response.data);
    } catch (error) {
      message.error(t('deals:fetch-payments-error', 'Failed to fetch deal payments'));
    } finally {
      updateLoadingState('payments', false);
    }
  }, [id, dealBasic, t, updateLoadingState]);

  // Check if deal can be edited
  const checkEditability = useCallback(() => {
    if (!dealBasic || !dealPayments) return;

    const canEditDeal = dealBasic.isActive &&
      !dealBasic.closedAt &&
      (!dealPayments.paidAmount || dealPayments.paidAmount === 0);

    setCanEdit(canEditDeal ? true : false);
  }, [dealBasic, dealPayments]);

  // Load basic details on mount
  useEffect(() => {
    fetchBasicDetails();
  }, [fetchBasicDetails]);

  // Load additional data after basic details
  useEffect(() => {
    if (dealBasic) {
      // Load units and payments in parallel
      Promise.all([
        fetchUnits(),
        fetchPayments()
      ]);
    }
  }, [dealBasic, fetchUnits, fetchPayments]);

  // Check editability when we have all required data
  useEffect(() => {
    checkEditability();
  }, [checkEditability]);

  const steps = [
    {
      title: t('deals:select-client', 'Select Client'),
      content: (
        <ClientSelection
          value={formData.clientId}
          onChange={clientId => setFormData(prev => ({ ...prev, clientId }))}
        />
      ),
    },
    {
      title: t('deals:select-details', 'Deal Details'),
      content: loadingStates.units ? (
        <LoadingContainer>
          <Spin size="large" />
          <div style={{ marginTop: '16px', color: 'rgba(0, 0, 0, 0.45)' }}>
            {t('deals:loading-units', 'Loading deal units...')}
          </div>
        </LoadingContainer>
      ) : (
        <ProjectAndUnitSelection
          value={{
            projectId: formData.projectId,
            units: formData.units || [],
            pricingMode: dealBasic?.pricingMode,
          }}
          onChange={({ projectId, units, stage, pricingMode }) =>
            setFormData(prev => ({ ...prev, projectId, units, stage, pricingMode }))
          }
          editMode={true}
          currentDealId={Number(id)}
          deal={dealBasic ? {
            ...dealBasic,
            units: dealUnits,
            payments: dealPayments
          } : undefined}
        />
      ),
    },
  ];

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const validUnits = formData.units?.filter(unit => unit.quantity && unit.quantity > 0);
      await dealsApi.apiDealsIdPut({
        id: Number(id),
        updateDealCommand: {
          ...formData,
          units: validUnits,
        } as UpdateDealCommand,
      });
      message.success(t('deals:updated', 'Deal updated successfully'));
      navigate(`/deals/${id}`);
    } catch (error) {
      message.error(t('deals:update-error', 'Failed to update deal'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/deals/${id}`);
  };

  const validateStep = () => {
    switch (currentStep) {
      case 0:
        return !!formData.clientId;
      case 1:
        return formData.projectId && formData.units && formData.units.length > 0;
      default:
        return true;
    }
  };

  const handleNext = () => {
    setCurrentStep(current => current + 1);
  };

  const handleBack = () => {
    setCurrentStep(current => current - 1);
  };

  // Show loading state while fetching basic details
  if (loadingStates.basic) {
    return (
      <>
        <PageHeader
          ghost
          title={t('deals:edit-deal', 'Edit Deal')}
          subTitle={t('deals:edit-deal-subtitle', 'Modify deal details and units')}
        />
        <Main>
          <Card>
            <LoadingContainer>
              <Spin size="large" />
              <div style={{ marginTop: '16px', color: 'rgba(0, 0, 0, 0.45)' }}>
                {t('deals:loading-deal', 'Loading deal information...')}
              </div>
            </LoadingContainer>
          </Card>
        </Main>
      </>
    );
  }

  // Show error state if deal cannot be edited
  if (canEdit === false) {
    return (
      <>
        <PageHeader
          ghost
          title={t('deals:edit-deal', 'Edit Deal')}
          subTitle={t('deals:edit-deal-subtitle', 'Modify deal details and units')}
        />
        <Main>
          <Card>
            <div className="text-center py-8">
              <FeatherIcon icon="alert-circle" size={48} className="mb-4" />
              <h3>{t('deals:cannot-edit', 'Cannot Edit Deal')}</h3>

              {!dealBasic?.isActive && (
                <Alert
                  type="warning"
                  message={t('deals:inactive', 'This deal is inactive and cannot be modified')}
                  style={{ marginBottom: '16px' }}
                />
              )}

              {dealBasic?.closedAt && (
                <Alert
                  type="warning"
                  message={t('deals:closed', 'This deal has been closed and cannot be modified')}
                  style={{ marginBottom: '16px' }}
                />
              )}

              {dealPayments?.paidAmount && dealPayments.paidAmount > 0 && (
                <Alert
                  type="warning"
                  message={t('deals:has-payments', 'This deal has received payments and cannot be modified')}
                  style={{ marginBottom: '16px' }}
                />
              )}

              <Button type="primary" onClick={() => navigate(`/deals/${id}`)} className="mt-4">
                {t('deals:back-to-details', 'Back to Deal Details')}
              </Button>
            </div>
          </Card>
        </Main>
      </>
    );
  }

  // Show loading while checking editability
  if (canEdit === null) {
    return (
      <>
        <PageHeader
          ghost
          title={t('deals:edit-deal', 'Edit Deal')}
          subTitle={t('deals:edit-deal-subtitle', 'Modify deal details and units')}
        />
        <Main>
          <Card>
            <LoadingContainer>
              <Spin size="large" />
              <div style={{ marginTop: '16px', color: 'rgba(0, 0, 0, 0.45)' }}>
                {t('deals:checking-permissions', 'Checking edit permissions...')}
              </div>
            </LoadingContainer>
          </Card>
        </Main>
      </>
    );
  }

  return (
    <>
      <PageHeader
        ghost
        title={t('deals:edit-deal', 'Edit Deal')}
        subTitle={t('deals:edit-deal-subtitle', 'Modify deal details and units')}
      />

      <Main>
        <StyledForm>
          <Card>
            <Steps current={currentStep} items={steps} className="mb-8" />

            <StepsContent>{steps[currentStep].content}</StepsContent>

            <div className="border-t pt-4 mt-8" style={{ float: 'right' }}>
              <Space>
                <Button onClick={handleCancel}>{t('global:cancel', 'Cancel')}</Button>
                {currentStep > 0 && <Button onClick={handleBack}>{t('global:back', 'Back')}</Button>}
                {currentStep < steps.length - 1 && (
                  <Button
                    type="primary"
                    onClick={handleNext}
                    disabled={!validateStep() || loadingStates.units}
                    loading={loadingStates.units && currentStep === 0}
                  >
                    {t('global:next', 'Next')}
                  </Button>
                )}
                {currentStep === steps.length - 1 && (
                  <Button
                    type="primary"
                    onClick={handleSubmit}
                    loading={submitting}
                    disabled={!validateStep() || loadingStates.units}
                  >
                    {t('global:save', 'Save')}
                  </Button>
                )}
              </Space>
            </div>
          </Card>
        </StyledForm>
      </Main>
    </>
  );
};

export default DealUpdatePage;